import { graphql } from 'gatsby'
import React from 'react'

import { CaseContent } from '../../@import-types/contentful/Case.types'
import { ContentfulEmployee } from '../../@import-types/contentful/Employee.types'
import ContentArea from '../../components/ContentArea'
import Layout from '../../components/Layout'
import TeaserCarouselBlock from '../../components/_blocks/TeaserCarouselBlock'
import EmployeeHero from '../../components/_heroes/EmployeeHero'
import IngressLayout from '../../components/_layouts/IngressLayout'
import LayoutContainer from '../../components/_layouts/LayoutContainer'
import Colors from '../../constants/Colors'
import { RootPageQuery } from '../../graphql/global.graphql'
import useMicroCopy from '../../hooks/globalContent/useMicroCopy'
import useIsDesktop from '../../hooks/useIsDesktop'
import { isDarkHexColor } from '../../utils/colorUtils'
import { renderTemplateString } from '../../utils/stringUtils'

import styles from './employee.module.scss'

type EmployeePageProps = RootPageQuery<{
  contentfulEmployee: ContentfulEmployee
  allContentfulCasePage: {
    edges: Array<{
      node: CaseContent
    }>
  }
}>

export default function EmployeePage({ data, pageContext }: EmployeePageProps) {
  const { contentfulEmployee, allContentfulCasePage, commonContent } = data

  const {
    content,
    specialities,
    introduction,
    heroImage,
    color,
  } = contentfulEmployee

  const isDesktop = useIsDesktop()

  const projectsCarouelLabelTemplate = useMicroCopy(
    'text.persons_previous_projects',
    commonContent
  )
  const specialitiesLabel = useMicroCopy('text.specialities', commonContent)

  const cases = allContentfulCasePage.edges.map((edge) => edge.node)

  const textColor =
    !heroImage && color && !isDarkHexColor(color) ? Colors.Brown : Colors.White

  const renderedIngress = (
    <IngressLayout
      listLabel={specialitiesLabel}
      listChildren={specialities}
      text={introduction}
      isSmallerText={!isDesktop}
    />
  )

  return (
    <Layout
      data={data}
      basePageContent={contentfulEmployee}
      pageContext={pageContext}
      pageTextColor={textColor}
    >
      <EmployeeHero
        employee={contentfulEmployee}
        ingressChildren={isDesktop ? renderedIngress : null}
      />

      {!isDesktop && (
        <LayoutContainer className={styles.ingressContainer} withMargin>
          {renderedIngress}
        </LayoutContainer>
      )}

      {content && <ContentArea content={content} />}

      {cases.length > 0 && (
        <TeaserCarouselBlock
          title={renderTemplateString(projectsCarouelLabelTemplate, {
            name: contentfulEmployee.firstName,
          })}
          teasers={cases}
        />
      )}
    </Layout>
  )
}

export const pageQuery = graphql`
  query EmployeeeById($id: String!, $startPageId: String!, $locale: String!) {
    contentfulEmployee(id: { eq: $id }) {
      id

      firstName
      lastName

      role
      color

      specialities
      introduction {
        json
      }

      office {
        city
      }

      heroImage: image {
        title
        description
        fluid(quality: 90, maxWidth: 1800) {
          ...GatsbyContentfulFluid_withWebp
        }
        file {
          details {
            image {
              height
              width
            }
          }
        }
      }

      heroImageMobile {
        title
        description
        fluid(quality: 90, maxWidth: 900) {
          ...GatsbyContentfulFluid_withWebp
        }
      }

      heroImageFocalPoint {
        focalPoint {
          x
          y
        }
      }

      content {
        __typename

        ... on Node {
          ...ContentAreaFragment
        }
      }
    }

    ...CasesByEmployee

    ...GlobalFragment
  }
`
